import { getRefidsForSeries } from '@/api'
import CollectionDataGrid from '@/components/CollectionDataGrid'
import NumberField from '@/components/NumberField'
import RfidsTable from '@/components/RfidsTable'
import { pb, pbUpsert, useCollection } from '@/pb'
import { arrayToObject, objectMap, ruDateTime, useCheckboxState, useInputState } from '@/utils'
import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, FormLabel, InputLabel, LinearProgress, MenuItem, Select } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'

const Editor = ({ onClose }) => {
  const [manufacturers, manufacturersLoaded] = useCollection('manufacturers')
  const [products, productsLoaded] = useCollection('products')

  const [manufacturer, setManufacturer] = useInputState()
  const [product, setProduct] = useInputState()
  const [count, setCount] = useInputState(1)
  const [initialAmount, setInitialAmount] = useInputState(5)
  const [reusable, setReusable] = useCheckboxState(false)

  useEffect(() => setProduct(''), [manufacturer])

  const loaded = productsLoaded && manufacturersLoaded
  const canSave = Boolean(manufacturer && product && count && (!reusable ? true : initialAmount))

  if (!loaded) return <LinearProgress />

  return <>
    <DialogTitle>Серия</DialogTitle>
    <DialogContent className="editorForm">
      <FormControl>
        <InputLabel id="manufacturer">{fields.manufacturerName.headerName}</InputLabel>
        <Select
          labelId="manufacturer"
          value={manufacturer}
          label={fields.manufacturerName.headerName}
          onChange={setManufacturer}
        >
          {Object.entries(manufacturers).map(([id, { name }]) =>
            <MenuItem value={id}>{name}</MenuItem>
          )}
        </Select>
      </FormControl>
      <FormControl>
        <InputLabel id="product">{fields.productName.headerName}</InputLabel>
        <Select
          labelId="product"
          disabled={!manufacturer}
          value={manufacturer ? product : ''}
          label={fields.productName.headerName}
          onChange={setProduct}
        >
          {Object.entries(products).filter(v => v[1].manufacturer === manufacturer).map(([id, { name }]) =>
            <MenuItem value={id}>{name}</MenuItem>
          )}
        </Select>
      </FormControl>

      <NumberField label={fields.count.headerName} min={1} max={2 ** 24} value={count} onValueChange={setCount} />
      <NumberField label={fields.initialAmount.headerName} min={0} max={2 ** 16} value={initialAmount} onValueChange={setInitialAmount} />

      <FormControl>
        <FormLabel component="legend">Опции</FormLabel>
        <FormGroup>
          <FormControlLabel label={fields.reusable.headerName} control={
            <Checkbox checked={reusable} onChange={setReusable} />
          } />
        </FormGroup>
      </FormControl>

    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} >Отмена</Button>
      <Button onClick={() => pbUpsert('series', { manufacturer, product, count, initialAmount, reusable }).then(onClose)} variant="contained" disabled={!canSave}>Сохранить</Button>
    </DialogActions>
  </>
}

const productionStart = seriesId => () => {
  pb.collection('series').update(seriesId, {
    productionStarted: new Date()
  })
}

const productionFinish = seriesId => () => {
  pb.collection('series').update(seriesId, {
    productionFinished: new Date()
  })
}

const columns = [
  { field: 'manufacturerName', headerName: 'Производитель', width: 200 },
  { field: 'productName', headerName: 'Продукт', width: 200 },
  {
    field: 'productionStarted', headerName: 'Начало производства', width: 150,
    valueFormatter: ruDateTime,
    renderCell: ({ id, formattedValue }) => formattedValue || <Button onClick={productionStart(id)} variant="contained">Начать</Button>
  },
  {
    field: 'productionFinished', headerName: 'Окончание производства', width: 150,
    valueFormatter: ruDateTime,
    renderCell: ({ id, formattedValue, row }) => formattedValue || <Button disabled={!row.productionStarted} onClick={productionFinish(id)} variant="contained">Завершить</Button>
  },
  { field: 'count', headerName: 'Количество', width: 70 },
  { field: 'first', headerName: 'Первый', width: 70 },
  { field: 'last', headerName: 'Последний', width: 70 },
  { field: 'initialAmount', headerName: 'Обьём (мл)', width: 100, valueFormatter: v => v ? v : '-' },
  { field: 'reusable', headerName: 'Многоразовый', width: 120, valueFormatter: v => v ? 'да' : '', },
]
const fields = arrayToObject(columns, 'field')

export default function Series({ title }) {

  const [series, seriesLoaded] = useCollection('series')
  const [products, productsLoaded] = useCollection('products')
  const [manufacturers, manufacturersLoaded] = useCollection('manufacturers')

  const [rfids, setRfids] = useState([])
  const [rfidsLoading, setRfidsLoading] = useState(false)

  const handleSelect = useCallback(selectionModel => {
    const ids = [...selectionModel.ids]
    setRfids([])
    if (!ids.length) return

    setRfidsLoading(true)
    getRefidsForSeries(ids).then(rfids => {
      setRfids(rfids.split('\n').map(v => v.split(',')))
      setRfidsLoading(false)
    }).catch(err => {
      setRfidsLoading(false)
    })
  }, [])

  const productsWithManufacturers = useMemo(() => {
    return objectMap(products, product => ({
      ...product,
      manufacturer: manufacturers[product.manufacturer]?.id,
      manufacturerName: manufacturers[product.manufacturer]?.name
    }))
  }, [products, manufacturers])

  const seriesWithProducts = useMemo(() => {
    return objectMap(series, ser => ({
      ...ser,
      productName: productsWithManufacturers[ser.product]?.name,
      manufacturerName: productsWithManufacturers[ser.product]?.manufacturerName,
    }))
  }, [series, manufacturers])

  return <>
    <CollectionDataGrid
      collectionName={title}
      collection={seriesWithProducts}
      columns={columns}
      loading={!seriesLoaded || !productsLoaded || !manufacturersLoaded}
      onSelect={handleSelect}
      editor={Editor}
      editable={false}
      creatable={true}
    />
    <RfidsTable
      series={seriesWithProducts}
      collectionName="Метки"
      rfids={rfids}
      loading={rfidsLoading}
    />
  </>

}